import {createAction, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {GIFT_CARD_DEFAULT_VALUE} from '~/CreateFabricGreetingCard/utils/constants';
import {
  INIT_PRODUCT_CONFIG_ID,
  STORE_E_GIFT_CARDS,
  UPDATE_E_GIFT_CARD_RETAILER,
  UPDATE_E_GIFT_CARD_USER_DISABLED,
  UPDATE_E_GIFT_CARD_VALUE,
} from '~/common/fabric/GiftCard/reducers/eGiftCard';
import {
  TOGGLE_DELIVER_CARD_NOW,
  UPDATE_DELIVER_CARD_DATE_TIME,
  UPDATE_SENDER,
} from '~/common/fabric/reducers/review/constants';
import {FabricTemplate} from '~/common/fabric/types';
import {getTimezone} from '~/common/utils';
import {fabricDesignWithCrossOriginUpdates} from '~/common/utils/event_utils';
import {
  CardType,
  Ecard,
  EcardEventData,
  EcardSenderData,
  EventOptionsDesign,
  EventOptionsWrite,
  EventStatus,
} from '~/QuickEcards/types/ecard';
import {GiftCardRetailer} from '~/QuickEcards/types/egift';
import {Tab} from '~/QuickEcards/types/misc';
import {logSelectSendCardLater, logSelectSendCardNow} from '~/QuickEcards/utils/logging';
import {updateGlobalMessage} from './recipientMessages';

const timezone = getTimezone();

const updateDeliverCardNowStatus = createAction(TOGGLE_DELIVER_CARD_NOW);
const updateDeliverCardDateTime = createAction(UPDATE_DELIVER_CARD_DATE_TIME);
const updateSenderNameInCheckout = createAction(UPDATE_SENDER);
const externalUpdateGlobalMessageAction = createAction<string>(updateGlobalMessage.type);
const isGiftCardDisabledByUser = createAction(UPDATE_E_GIFT_CARD_USER_DISABLED);
const updateGiftCardValue = createAction(UPDATE_E_GIFT_CARD_VALUE);
const updateGiftCardRetailer = createAction(UPDATE_E_GIFT_CARD_RETAILER);
const initializeProductConfigId = createAction(INIT_PRODUCT_CONFIG_ID);
const storeEGiftCardRetailers = createAction(STORE_E_GIFT_CARDS);

interface EcardState extends Ecard {
  isInEditMode: boolean;
  // rsvp_off: boolean;
  pristine: boolean;
}

const initialState: EcardState = {
  // ...
  id: '',
  // biome-ignore lint/style/useNamingConvention: existing error
  event_data: {
    // biome-ignore lint/style/useNamingConvention: existing error
    event_host_name: '',
    // biome-ignore lint/style/useNamingConvention: existing error
    event_type: '',
    status: EventStatus.DRAFT,
    // biome-ignore lint/style/useNamingConvention: existing error
    used_as: CardType.CARD,
    // biome-ignore lint/style/useNamingConvention: existing error
    user_saved: true,
    // biome-ignore lint/style/useNamingConvention: existing error
    system_generated: false,
    // biome-ignore lint/style/useNamingConvention: existing error
    known_timezone_name:
      timezone?.timezoneId ?? Intl?.DateTimeFormat().resolvedOptions().timeZone ?? '',
    // biome-ignore lint/style/useNamingConvention: existing error
    is_pro: null,
    options: {
      design: {
        background: '',
        // biome-ignore lint/style/useNamingConvention: existing error
        background_image_desktop: '',
        // biome-ignore lint/style/useNamingConvention: existing error
        background_image_mobile: '',
        // biome-ignore lint/style/useNamingConvention: existing error
        overlay_id: null,
        // biome-ignore lint/style/useNamingConvention: existing error
        overlay_video_mobile_vp9: '',
        // biome-ignore lint/style/useNamingConvention: existing error
        overlay_video_desktop_vp9: '',
        // biome-ignore lint/style/useNamingConvention: existing error
        overlay_video_mobile_h265: '',
        // biome-ignore lint/style/useNamingConvention: existing error
        overlay_video_desktop_h265: '',
        // biome-ignore lint/style/useNamingConvention: existing error
        reveal_id: null,
        // biome-ignore lint/style/useNamingConvention: existing error
        reveal_video_mobile_vp9: '',
        // biome-ignore lint/style/useNamingConvention: existing error
        reveal_video_desktop_vp9: '',
        // biome-ignore lint/style/useNamingConvention: existing error
        reveal_video_mobile_h265: '',
        // biome-ignore lint/style/useNamingConvention: existing error
        reveal_video_desktop_h265: '',
      },
      // biome-ignore lint/style/useNamingConvention: existing error
      event_option_disable_rokt: false,
      write: {
        // biome-ignore lint/style/useNamingConvention: existing error
        text_layer_back: '',
      },
    },
    // biome-ignore lint/style/useNamingConvention: existing error
    gift_card_data: {
      // biome-ignore lint/style/useNamingConvention: existing error
      amount_in_cents: 0,
      // biome-ignore lint/style/useNamingConvention: existing error
      product_config_id: '',
      // biome-ignore lint/style/useNamingConvention: existing error
      product_config_name: '',
      image: '',
      // biome-ignore lint/style/useNamingConvention: existing error
      user_disabled: true,
    },
  },
  // biome-ignore lint/style/useNamingConvention: existing error
  sender_data: {
    // biome-ignore lint/style/useNamingConvention: existing error
    host_id: '',
  },
  analytics: {
    // biome-ignore lint/style/useNamingConvention: existing error
    tracking_product_type: 'premium',
    // biome-ignore lint/style/useNamingConvention: existing error
    tracking_data: {gallery_category: ''},
  },
  isInEditMode: false,
  pristine: true,
  // biome-ignore lint/style/useNamingConvention: existing error
  template_data: {
    // biome-ignore lint/style/useNamingConvention: existing error
    template_name: '',
    title: '',
  },
  // This key will be used in other pages (besides QuickECard)
  // biome-ignore lint/style/useNamingConvention: existing error
  is_ecard: true,
};

const ecardSlice = createSlice({
  name: 'ecard',
  initialState,
  reducers: {
    loadFabricTemplate: (state, action: PayloadAction<FabricTemplate>): EcardState => ({
      ...state,
      // biome-ignore lint/style/useNamingConvention: existing error
      event_data: {
        ...state.event_data,
        // biome-ignore lint/style/useNamingConvention: existing error
        fabric_design_data:
          fabricDesignWithCrossOriginUpdates(action.payload.editable_art.default) ??
          state.event_data.fabric_design_data,
      },
      // biome-ignore lint/style/useNamingConvention: existing error
      template_data: {
        // biome-ignore lint/style/useNamingConvention: existing error
        template_name: action.payload.template_name,
        title: action.payload.display_name,
      },
    }),
    loadEcard: (state, action: PayloadAction<Partial<Ecard>>): EcardState => ({
      ...state,
      ...action.payload,
      // biome-ignore lint/style/useNamingConvention: existing error
      event_data: {
        ...state.event_data,
        ...(action.payload.event_data ?? {}),
        // biome-ignore lint/style/useNamingConvention: existing error
        gift_card_data:
          action.payload.event_data?.gift_card_data ?? state.event_data.gift_card_data,
        // biome-ignore lint/style/useNamingConvention: existing error
        fabric_design_data:
          fabricDesignWithCrossOriginUpdates(action.payload.event_data?.fabric_design_data) ??
          state.event_data.fabric_design_data,
        options: {
          ...state.event_data.options,
          ...action.payload.event_data?.options,
          design: {
            ...state.event_data.options.design,
            ...action.payload.event_data?.options.design,
            // biome-ignore lint/style/useNamingConvention: existing error
            reveal_id:
              action.payload.event_data?.options.design.reveal_id ??
              state.event_data.options.design.reveal_id ??
              '',
            // biome-ignore lint/style/useNamingConvention: existing error
            overlay_id:
              action.payload.event_data?.options.design.overlay_id ??
              state.event_data.options.design.overlay_id ??
              '',
          },
        },
      },
      isInEditMode:
        !!action.payload.event_data?.status &&
        action.payload.event_data.status !== EventStatus.DRAFT,
      pristine: !action.payload?.id,
    }),
    updateEcard: (state, action: PayloadAction<Partial<EcardState>>): EcardState => ({
      ...state,
      ...action.payload,
      // biome-ignore lint/style/useNamingConvention: existing error
      event_data: {
        ...state.event_data,
        // biome-ignore lint/style/useNamingConvention: existing error
        fabric_design_data:
          fabricDesignWithCrossOriginUpdates(action.payload.event_data?.fabric_design_data) ??
          state.event_data.fabric_design_data,
      },
      pristine: false,
    }),
    updateEventData: (state, action: PayloadAction<Partial<EcardEventData>>) => ({
      ...state,
      // biome-ignore lint/style/useNamingConvention: existing error
      event_data: {
        ...state.event_data,
        ...action.payload,
        // biome-ignore lint/style/useNamingConvention: existing error
        fabric_design_data:
          fabricDesignWithCrossOriginUpdates(action.payload?.fabric_design_data) ??
          state.event_data.fabric_design_data,
      },
      pristine: false,
    }),
    updateSenderData: (state, action: PayloadAction<Partial<EcardSenderData>>) => ({
      ...state,
      // biome-ignore lint/style/useNamingConvention: existing error
      event_data: {
        ...state.event_data,
        ...action.payload,
      },
      pristine: false,
    }),
    updateEventDesignOptions: (state, action: PayloadAction<Partial<EventOptionsDesign>>) => ({
      ...state,
      // biome-ignore lint/style/useNamingConvention: existing error
      event_data: {
        ...state.event_data,
        options: {
          ...state.event_data.options,
          design: {
            ...state.event_data.options.design,
            ...action.payload,
          },
        },
      },
    }),
    updateEventWriteOptions: (state, action: PayloadAction<Partial<EventOptionsWrite>>) => ({
      ...state,
      // biome-ignore lint/style/useNamingConvention: existing error
      event_data: {
        ...state.event_data,
        options: {
          ...state.event_data.options,
          write: {
            ...state.event_data.options.write,
            ...action.payload,
          },
        },
      },
    }),
    updateTemplateData: (state, action) => ({
      ...state,
      // biome-ignore lint/style/useNamingConvention: existing error
      template_data: {
        ...state.template_data,
        ...action.payload,
      },
      pristine: false,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateDeliverCardNowStatus, (state, action: PayloadAction<boolean | undefined>) => {
        // If "Send eCard Now" has been turned on, reset send_on, otherwise leave as-is
        if (action.payload) {
          state.event_data.send_on = null;
          logSelectSendCardNow(state.id);
        } else {
          logSelectSendCardLater(state.id);
        }
      })
      .addCase(
        updateDeliverCardDateTime,
        (state, action: PayloadAction<{formattedDate: string; isoDate: string} | undefined>) => {
          state.event_data.send_on = action.payload?.isoDate
            ? action.payload.isoDate.slice(0, action.payload.isoDate.length - 1)
            : action.payload?.formattedDate;
        }
      )
      .addCase(updateSenderNameInCheckout, (state, action: PayloadAction<string | undefined>) => {
        state.event_data.event_host_name = action.payload ?? '';
      })
      .addCase(
        externalUpdateGlobalMessageAction,
        (state, action: PayloadAction<string | undefined>) => {
          state.event_data.options.write.text_layer_back = action.payload ?? null;
        }
      )
      .addCase(isGiftCardDisabledByUser, (state, action: PayloadAction<boolean | undefined>) => {
        state.event_data.gift_card_data.user_disabled = action.payload ?? true;
        if (state.event_data.gift_card_data.user_disabled) {
          state.event_data.gift_card_data.amount_in_cents = 0;
          state.event_data.gift_card_data.product_config_id = '';
          state.event_data.gift_card_data.product_config_name = '';
          state.event_data.gift_card_data.image = '';
          state.event_data.used_as = CardType.CARD;
        } else {
          state.event_data.used_as = CardType.GIFTCARD;
        }
      })
      .addCase(updateGiftCardValue, (state, action: PayloadAction<number | undefined>) => {
        state.event_data.gift_card_data.amount_in_cents = action.payload ?? 0;
      })
      .addCase(
        updateGiftCardRetailer,
        (state, action: PayloadAction<GiftCardRetailer | undefined>) => {
          if (!action.payload) return;
          state.event_data.gift_card_data.product_config_id = action.payload.product_config_id;
          state.event_data.gift_card_data.product_config_name = action.payload.product_config_name;
          state.event_data.gift_card_data.image = action.payload.image;
          state.event_data.gift_card_data.amount_in_cents = action.payload.product_config_id
            ? GIFT_CARD_DEFAULT_VALUE
            : 0;
          state.event_data.gift_card_data.user_disabled = false;
          state.event_data.used_as = action.payload.product_config_id
            ? CardType.GIFTCARD
            : CardType.CARD;
        }
      )
      .addCase(initializeProductConfigId, (state, action: PayloadAction<string | undefined>) => {
        state.event_data.gift_card_data.product_config_id = action.payload ?? '';
        state.event_data.used_as = action.payload ? CardType.GIFTCARD : CardType.CARD;
      })
      .addCase(
        storeEGiftCardRetailers,
        (state, action: PayloadAction<GiftCardRetailer[] | undefined>) => {
          if (!action.payload) return;
          const retailer = action.payload.find(
            (ret) => ret.product_config_id === state.event_data.gift_card_data.product_config_id
          );
          if (!retailer) return;
          state.event_data.gift_card_data.product_config_id = retailer.product_config_id;
          state.event_data.gift_card_data.product_config_name = retailer.product_config_name;
          state.event_data.gift_card_data.image = retailer.image;
          state.event_data.gift_card_data.user_disabled = false;
          state.event_data.gift_card_data.amount_in_cents = GIFT_CARD_DEFAULT_VALUE;
          state.event_data.used_as = CardType.GIFTCARD;
        }
      )
      // biome-ignore lint/suspicious/noEmptyBlockStatements: existing error
      .addDefaultCase(() => {});
  },
  selectors: {
    selectEventData: (ecard) => ecard.event_data,
    selectEcardId: (ecard) => ecard.id,
    selectEcardHostId: (ecard) => ecard.sender_data.host_id,
    selectEcardHostName: (ecard) => ecard.event_data.event_host_name,
    selectEcardEventType: (ecard) => ecard.event_data.event_type,
    selectShortLink: (ecard) => ecard.event_data.shortlink,
    selectFabricArt: (ecard) => ecard.event_data.fabric_design_data,
    selectBackground: (ecard, isDesktop: boolean = false) => {
      const url = isDesktop
        ? ecard.event_data.options.design.background_image_desktop
        : ecard.event_data.options.design.background_image_mobile;
      if (url) return `url(${url})`;
      return ecard.event_data.options.design.background || window.fabricTemplate.background_color;
    },
    selectGiftCardData: (ecard) => ecard.event_data.gift_card_data,
    selectGiftCardProductConfigId: (ecard) => ecard.event_data.gift_card_data.product_config_id,
    selectHasGiftCard: (ecard) => !!ecard.event_data.gift_card_data.product_config_id,
    selectFabricArtBackgroundImage: (ecard) => ecard.event_data.fabric_design_data?.backgroundImage,
    selectPristine: (ecard) => ecard.pristine,
    selectIsInEditMode: (ecard) => ecard.isInEditMode,
    selectTemplateName: (ecard) => ecard.template_data.template_name,
    selectTemplateTitle: (ecard) => ecard.template_data.title,
    selectSelectedReveal: (ecard) => ecard.event_data.options.design.reveal_id,
    selectSelectedOverlay: (ecard) => ecard.event_data.options.design.overlay_id,
    selectSelectedAsset: (ecard, type: Tab): string | null => {
      if (!type) return null;
      switch (type) {
        case 'reveals': {
          return ecardSlice.getSelectors().selectSelectedReveal(ecard);
        }
        case 'effects': {
          return ecardSlice.getSelectors().selectSelectedOverlay(ecard);
        }
        case 'design': {
          return ecardSlice.getSelectors().selectTemplateName(ecard);
        }
        default: {
          return null;
        }
      }
    },
    selectGlobalMessage: (ecard) => ecard.event_data.options.write.text_layer_back,
    selectCardSentOn: (ecard) => ecard.event_data.sent_on,
    selectEcardIsDraft: (ecard) => ecard.event_data.status === EventStatus.DRAFT,
  },
});

export const {
  loadFabricTemplate,
  loadEcard,
  updateEcard,
  updateEventData,
  updateSenderData,
  updateEventDesignOptions,
  updateEventWriteOptions,
  updateTemplateData,
} = ecardSlice.actions;
export const {
  selectEventData,
  selectEcardId,
  selectEcardHostName,
  selectEcardHostId,
  selectEcardEventType,
  selectShortLink,
  selectFabricArt,
  selectBackground,
  selectGiftCardData,
  selectGiftCardProductConfigId,
  selectHasGiftCard,
  selectFabricArtBackgroundImage,
  selectPristine,
  selectIsInEditMode,
  selectTemplateName,
  selectTemplateTitle,
  selectSelectedReveal,
  selectSelectedOverlay,
  selectSelectedAsset,
  selectGlobalMessage,
  selectCardSentOn,
  selectEcardIsDraft,
} = ecardSlice.selectors;
export const ecardReducer = ecardSlice.reducer;
